import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { StatusBadge } from '../components/ui';
import { SessionLogViewer } from '../components/sessions/SessionLogViewer';

function outcomeTone(outcome: string | null): 'success' | 'danger' | 'neutral' {
  if (!outcome) return 'neutral';
  if (outcome === 'task_complete') return 'success';
  if (outcome === 'crashed' || outcome === 'error') return 'danger';
  return 'neutral';
}

// ---------------------------------------------------------------------------
// Main page — breadcrumb + session metadata chrome. The log fetching and
// rendering itself lives in SessionLogViewer (UX-04), shared with the epic
// drawer's "Raw logs" tab.
// ---------------------------------------------------------------------------

export const SessionLogsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  // `task` is the ECS task ID (URL hygiene — never the full ARN, which embeds
  // the AWS account/region/cluster). `taskArn` is the legacy param accepted
  // so pre-existing bookmarks keep working; it's reduced to the ID here.
  const legacyArn = searchParams.get('taskArn') ?? '';
  const task = searchParams.get('task')
    ?? (legacyArn.includes('/') ? (legacyArn.split('/').pop() as string) : legacyArn);
  const agent    = searchParams.get('agent')    ?? '';
  const repo     = searchParams.get('repo')     ?? '';
  const story    = searchParams.get('story')    ?? '';
  const epic     = searchParams.get('epic')     ?? '';
  const outcome  = searchParams.get('outcome')  ?? null;
  const startedAt = searchParams.get('started') ?? undefined;
  const finishedAt = searchParams.get('finished') ?? undefined;
  const epicRef  = searchParams.get('epicRef')  ?? '';

  // Epic link — prefer epicRef (cross-repo), fall back to numeric epic
  const epicTo = epicRef
    ? `/epics/${encodeURIComponent(epicRef)}`
    : epic ? `/epics/${epic}` : null;

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <Header title="Session logs" />

      {/* ── Breadcrumb + metadata strip ─────────────────────────── */}
      <div className="bg-slate-800 border-b border-slate-700 px-4 py-3 space-y-2">
        <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
          <Link to="/today" className="hover:text-slate-200">← Today</Link>
          {epicTo && (
            <>
              <span className="text-slate-600">·</span>
              <Link to={epicTo} className="hover:text-slate-200">
                {repo} #{epic}
              </Link>
            </>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-sm text-white font-semibold">{agent}</span>
          {story && (
            <span className="text-xs text-slate-400 font-mono">story {story}</span>
          )}
          {outcome && (
            <StatusBadge tone={outcomeTone(outcome)}>{outcome}</StatusBadge>
          )}
          {!finishedAt && (
            <StatusBadge tone="inprogress" dot>live</StatusBadge>
          )}
          {startedAt && (
            <span className="text-xs text-slate-500">
              {new Date(startedAt).toLocaleTimeString()}
              {finishedAt ? ` → ${new Date(finishedAt).toLocaleTimeString()}` : ''}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0">
        <SessionLogViewer taskId={task} startedAt={startedAt} finishedAt={finishedAt} />
      </div>
    </div>
  );
};

export default SessionLogsPage;
